import { Box, Button, Stack, TextField } from "@mui/material";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import { Controller, useForm } from "react-hook-form";
import { useAddNewBankMutation } from "src/shared/api/bank";
import {
  IModalComponentProps,
  LoadingButton,
  ModalComponent,
} from "src/shared/components";
import { isApiError } from "src/shared/helpers/api.helpers";
import {
  IBankResponseItem,
  ICreateBankRequest,
} from "src/shared/types/bank.types";
import { EAgencies } from "src/shared/types/common.types";
import { messages } from "src/shared/utils/hookFormRules";
import { bankNameRulePattern } from "src/shared/utils/hookFormRules/rules";

interface IAddBankModalProps
  extends Omit<IModalComponentProps, "children" | "title" | "modalFooter"> {
  agency: EAgencies;
  onSuccess?: (bank: IBankResponseItem) => void;
}

export function AddBankModal({
  agency,
  onSuccess,
  ...modalProps
}: IAddBankModalProps) {
  const methods = useForm<ICreateBankRequest>({
    mode: "onBlur",
    defaultValues: {
      agency,
      bic: "",
      name: "",
      address: "",
    },
  });

  const { control, handleSubmit } = methods;

  const [createBank, { isLoading }] = useAddNewBankMutation();
  const submitForm = async (data: ICreateBankRequest) => {
    try {
      const res = await createBank(data).unwrap();
      onSuccess?.(res);
      modalProps.onClose();
    } catch (e) {
      let message = "Произошла ошибка";
      if (isApiError(e)) {
        const errors = e.data?.errors;
        message = Object.values(errors).join("\r\n");
      }
      enqueueSnackbar(message, {
        variant: "error",
        handleClose: closeSnackbar,
      });
    }
  };

  return (
    <ModalComponent
      {...modalProps}
      title="Добавить банк"
      modalFooter={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Button variant="outlined" onClick={modalProps.onClose}>
            Отменить
          </Button>
          <LoadingButton
            onClick={handleSubmit(submitForm)}
            variant="contained"
            type="submit"
            isLoading={isLoading}
          >
            Применить
          </LoadingButton>
        </Box>
      }
    >
      <Box component="form" onSubmit={handleSubmit(submitForm)}>
        <Stack spacing={3} sx={{ pt: 3 }}>
          <Box>
            <Controller
              control={control}
              name="bic"
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  inputRef={ref}
                  fullWidth
                  {...field}
                  label="БИК (SWIFT)"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
              rules={{
                required: messages.required,
                pattern: {
                  value: /^[a-zA-Z0-9]{0,10}$/,
                  message: "Не более 10 cимволов [Aa-Zz 0-9]",
                },
              }}
            />
          </Box>
          <Box>
            <Controller
              control={control}
              name="name"
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  inputRef={ref}
                  fullWidth
                  {...field}
                  label="Наименование банка / Bank name"
                  error={!!error}
                  helperText={error?.message}
                  slotProps={{
                    htmlInput: {
                      maxLength: 255,
                    },
                  }}
                />
              )}
              rules={{
                required: messages.required,
                pattern: bankNameRulePattern,
              }}
            />
          </Box>
          <Box>
            <Controller
              control={control}
              name="address"
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  inputRef={ref}
                  fullWidth
                  {...field}
                  label="Адрес / Bank address"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
              rules={{
                required: messages.required,
                pattern: bankNameRulePattern,
              }}
            />
          </Box>
        </Stack>
      </Box>
    </ModalComponent>
  );
}
